<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.laborByProjectTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.laborByProjectSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.from') }}</label>
          <el-date-picker v-model="fromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.to') }}</label>
          <el-date-picker v-model="toDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <template v-else>
          <el-table :data="rows" stripe show-summary :summary-method="summaryRow">
            <el-table-column prop="projectName" :label="$t('common.project')" min-width="180" />
            <el-table-column prop="employeeCount" :label="$t('reports.employees')" align="center" min-width="100" />
            <el-table-column :label="$t('reports.normalHours')" align="right" min-width="110">
              <template #default="{ row }">{{ hrs(row.normalHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.afterHours')" align="right" min-width="110">
              <template #default="{ row }">{{ hrs(row.afterHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.overtimeHours')" align="right" min-width="110">
              <template #default="{ row }">{{ hrs(row.overtimeHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.redDayHours')" align="right" min-width="110">
              <template #default="{ row }">{{ hrs(row.redDayHours) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.totalHours')" align="right" min-width="110">
              <template #default="{ row }"><strong>{{ hrs(row.totalHours) }}</strong></template>
            </el-table-column>
          </el-table>

          <div class="d-flex justify-content-end mt-3">
            <el-pagination
              v-model:current-page="currentPage"
              :page-size="pageSize"
              :total="totalItems"
              layout="prev, pager, next"
              @current-change="fetchData"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- Labor billing statement: hours × project rate per end customer, with mark-as-billed -->
    <div class="card mt-4">
      <div class="card-body">
        <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h6 class="mb-0">{{ $t('reports.laborStatementTitle') }}</h6>
            <p class="mb-0 text-xs text-secondary">{{ $t('reports.laborStatementSubtitle') }}</p>
          </div>
          <material-button variant="outline" color="dark" size="sm" :disabled="!statementRows.length" @click="onExportStatement">
            <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
          </material-button>
        </div>

        <div v-if="statementLoading" class="text-center py-4">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <el-table v-else :data="statementRows" stripe>
          <el-table-column prop="endCustomerName" :label="$t('reports.endCustomer')" min-width="150">
            <template #default="{ row }">{{ row.endCustomerName || '—' }}</template>
          </el-table-column>
          <el-table-column :label="$t('common.project')" min-width="180">
            <template #default="{ row }">{{ (row.projectKey ? '[' + row.projectKey + '] ' : '') + (row.projectName || '') }}</template>
          </el-table-column>
          <el-table-column :label="$t('reports.hourlyRate')" align="right" min-width="130">
            <template #default="{ row }">
              <input
                type="number"
                min="0"
                step="1"
                class="form-control form-control-sm text-end"
                style="max-width:110px;display:inline-block"
                :value="row.hourlyLaborRate"
                @change="onRateChange(row, $event.target.value)"
              />
            </template>
          </el-table-column>
          <el-table-column :label="$t('reports.totalHours')" align="right" min-width="100">
            <template #default="{ row }">{{ hrs(row.totalHours) }}</template>
          </el-table-column>
          <el-table-column :label="$t('reports.billedHours')" align="right" min-width="100">
            <template #default="{ row }">{{ hrs(row.billedHours) }}</template>
          </el-table-column>
          <el-table-column :label="$t('reports.unbilledHours')" align="right" min-width="100">
            <template #default="{ row }"><strong>{{ hrs(row.unbilledHours) }}</strong></template>
          </el-table-column>
          <el-table-column :label="$t('reports.unbilledAmount')" align="right" min-width="120">
            <template #default="{ row }">{{ row.unbilledAmount != null ? amount(row.unbilledAmount) : '—' }}</template>
          </el-table-column>
          <el-table-column label="" width="140" align="right">
            <template #default="{ row }">
              <el-button size="small" type="primary" :disabled="row.unbilledHours <= 0 || markingBilled" @click="markBilled(row)">
                {{ $t('reports.markBilled') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";
import { ElMessageBox } from "element-plus";

export default {
  name: "LaborByProjectReport",
  components: { MaterialButton },
  data() {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - 29);
    return {
      fromDate: this.toIso(from),
      toDate: this.toIso(today),
      rows: [],
      totalItems: 0,
      totals: {},
      currentPage: 1,
      pageSize: 20,
      loading: true,
      statementRows: [],
      statementLoading: true,
      markingBilled: false,
    };
  },
  async mounted() {
    await Promise.allSettled([this.fetchData(), this.fetchStatement()]);
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    hrs(v) {
      return new Intl.NumberFormat("is-IS", { maximumFractionDigits: 1 }).format(v || 0);
    },
    reload() {
      this.currentPage = 1;
      this.fetchData();
      this.fetchStatement();
    },
    amount(v) {
      return new Intl.NumberFormat("is-IS", { maximumFractionDigits: 0 }).format(v || 0);
    },
    async fetchStatement() {
      this.statementLoading = true;
      try {
        const response = await api.get(`/Timesheet/labor-statement?dateFrom=${this.fromDate}&dateTo=${this.toDate}`);
        this.statementRows = response.data || [];
      } catch (e) {
        console.error("LaborByProjectReport fetchStatement error:", e); // eslint-disable-line no-console
      } finally {
        this.statementLoading = false;
      }
    },
    async onRateChange(row, value) {
      const rate = value === "" ? null : Number(value);
      if (rate != null && (Number.isNaN(rate) || rate < 0)) return;
      try {
        await api.put(`/Project/${row.projectId}/labor-rate`, { hourlyLaborRate: rate });
        await this.fetchStatement();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.$toast.danger(this.$t("common.unableToUpdate"), { timeout: 0 });
      }
    },
    async markBilled(row) {
      try {
        await ElMessageBox.confirm(
          this.$t("reports.markBilledConfirm", { hours: this.hrs(row.unbilledHours), project: row.projectName || row.projectKey }),
          this.$t("reports.markBilled"),
          { confirmButtonText: this.$t("reports.markBilled"), cancelButtonText: this.$t("common.cancel"), type: "warning" },
        );
      } catch {
        return;
      }
      this.markingBilled = true;
      try {
        await api.post("/Timesheet/labor-statement/mark-billed", {
          projectId: row.projectId,
          dateFrom: this.fromDate,
          dateTo: this.toDate,
        });
        await this.fetchStatement();
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
        this.$toast.danger(this.$t("common.unableToUpdate"), { timeout: 0 });
      } finally {
        this.markingBilled = false;
      }
    },
    onExportStatement() {
      const columns = [
        { key: "endCustomerName", label: this.$t("reports.endCustomer") },
        { key: "projectKey", label: this.$t("common.project") },
        { key: "projectName", label: this.$t("reports.projectName") },
        { key: "hourlyLaborRate", label: this.$t("reports.hourlyRate") },
        { key: "totalHours", label: this.$t("reports.totalHours") },
        { key: "billedHours", label: this.$t("reports.billedHours") },
        { key: "unbilledHours", label: this.$t("reports.unbilledHours") },
        { key: "unbilledAmount", label: this.$t("reports.unbilledAmount") },
      ];
      exportCsv(`labor-statement-${this.fromDate}_${this.toDate}`, columns, this.statementRows);
    },
    summaryRow() {
      // Footer reflects grand totals across ALL projects in the window, not just this page.
      return [
        this.$t("reports.allProjectsTotal"),
        "",
        this.hrs(this.totals.totalNormalHours),
        this.hrs(this.totals.totalAfterHours),
        this.hrs(this.totals.totalOvertimeHours),
        this.hrs(this.totals.totalRedDayHours),
        this.hrs(this.totals.totalHours),
      ];
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/Labor/ByProject", {
          params: { fromDate: this.fromDate, toDate: this.toDate, currentPage: this.currentPage, pageSize: this.pageSize },
        });
        this.rows = response.data.result || [];
        this.totalItems = response.data.totalItems || 0;
        this.totals = response.data;
      } catch (e) {
        console.error("LaborByProjectReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "projectName", label: this.$t("common.project") },
        { key: "employeeCount", label: this.$t("reports.employees") },
        { key: "normalHours", label: this.$t("reports.normalHours") },
        { key: "afterHours", label: this.$t("reports.afterHours") },
        { key: "overtimeHours", label: this.$t("reports.overtimeHours") },
        { key: "redDayHours", label: this.$t("reports.redDayHours") },
        { key: "totalHours", label: this.$t("reports.totalHours") },
      ];
      exportCsv(`labor-by-project-${this.fromDate}_${this.toDate}`, columns, this.rows);
    },
  },
};
</script>
